import { useEffect, useMemo } from "react";
import { Form, Select } from "antd";
import debounce from "lodash/debounce";
import useSelectOptions from "./useFetchOptions";

export default function DynamicSelect({
  name,
  label,
  rules = [],
  required = false,
  url,
  pageSize = 10,
  visible = true,
  placeholder = "Select an option",
  className = "my-form-input-antd-test",
  variant = "nested",
  labelKey = "name",
  valueKey = "id",
  initialValue,
}) {
  const { options, loading, hasMore, setSearch, setPage, setOptions } =
    useSelectOptions({
      baseURL: url,
      initialPage: 1,
      pageSize,
      load: visible,
    });

  useEffect(() => {
    if (initialValue?.[valueKey]) {
      setOptions((prev) => {
        const exists = prev.some(
          (opt) => opt[valueKey] === initialValue[valueKey]
        );
        if (exists) return prev;
        return [...prev, initialValue];
      });
    }
  }, [initialValue, valueKey, setOptions]);

  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        setPage(1);
        setSearch(val);
      }, 400),
    [setSearch, setPage]
  );

  const selectComponent = (
    <Select
      className={variant === "nested" ? className : undefined}
      placeholder={placeholder}
      showSearch
      filterOption={false}
      onSearch={debouncedSearch}
      onPopupScroll={(e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (
          scrollTop + clientHeight >= scrollHeight - 5 &&
          hasMore &&
          !loading
        ) {
          setPage((prev) => prev + 1);
        }
      }}
      loading={loading}
    >
      {options.map((opt) => (
        <Select.Option key={opt[valueKey]} value={opt[valueKey]}>
          {opt[labelKey]}
        </Select.Option>
      ))}
    </Select>
  );

  if (variant === "single") {
    return (
      <Form.Item
        name={name}
        label={label}
        rules={[
          ...(required
            ? [{ required: true, message: `${label} is required` }]
            : []),
          ...rules,
        ]}
      >
        {selectComponent}
      </Form.Item>
    );
  }

  return (
    <Form.Item
      label={<p className="my-form-lables">{label}</p>}
      rules={[
        ...(required
          ? [{ required: true, message: `${label} is required` }]
          : []),
        ...rules,
      ]}
    >
      <Form.Item name={name} noStyle>
        {selectComponent}
      </Form.Item>
    </Form.Item>
  );
}
